import { useCallback, useMemo, useRef, useState } from 'react';

import { useLazyGetBranchWorkTimesOnPeriodQuery } from '@app/api/Branches';
import type { TimeFormat } from '@app/api/Branches/types';
import { useLazyGetEmployeeRecordsQuery } from '@app/api/Records/api';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { formatTime, toMinutes } from '@pages/Calendar/utils';

import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '@pages/Calendar/data';
import type { CalendarEvent } from '@pages/Calendar/data';

import { mapIndividualCalendarApiData } from '../utils';

import type {
  IndividualCalendarDay,
  IndividualCalendarEmployeeMeta,
} from '../data';
import type { Moment } from 'moment';

interface LoadIndividualJournalParams {
  currentEmployeeId: number;
  periodStartMoment: Moment;
  periodEndMoment: Moment;
  visibleDays: Moment[];
}

interface UseIndividualCalendarDataResult {
  days: IndividualCalendarDay[];
  events: CalendarEvent[];
  currentEmployeeMeta: IndividualCalendarEmployeeMeta | null;
  isLoading: boolean;
  startTime: string;
  endTime: string;
  // Формат журнала филиала приходит тем же ответом, что и рабочее время
  // (GetBranchWorkTimesOnPeriod) — берём его из первого дня (issue/11162).
  timeFormat: TimeFormat | null;
  loadIndividualJournal(params: LoadIndividualJournalParams): Promise<void>;
}

const getMinTime = (values: string[], fallback: string) => {
  if (values.length === 0) {
    return fallback;
  }

  return formatTime(Math.min(...values.map(toMinutes)));
};

const getMaxTime = (values: string[], fallback: string) => {
  if (values.length === 0) {
    return fallback;
  }

  return formatTime(Math.max(...values.map(toMinutes)));
};

export const useIndividualCalendarData =
  (): UseIndividualCalendarDataResult => {
    const {
      params: { ActiveBranchId },
    } = useUserPrint();
    const { showNotification } = useNotification();
    const [getEmployeeRecords] = useLazyGetEmployeeRecordsQuery();
    const [getBranchWorkTimes] = useLazyGetBranchWorkTimesOnPeriodQuery();

    const [days, setDays] = useState<IndividualCalendarDay[]>([]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [timeFormat, setTimeFormat] = useState<TimeFormat | null>(null);
    const [currentEmployeeMeta, setCurrentEmployeeMeta] =
      useState<IndividualCalendarEmployeeMeta | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const requestIdRef = useRef(0);

    const loadIndividualJournal = useCallback(
      async ({
        currentEmployeeId,
        periodStartMoment,
        periodEndMoment,
        visibleDays,
      }: LoadIndividualJournalParams) => {
        const requestId = requestIdRef.current + 1;

        requestIdRef.current = requestId;

        if (!ActiveBranchId) {
          setDays([]);
          setEvents([]);
          setCurrentEmployeeMeta(null);
          setIsLoading(false);

          return;
        }

        setIsLoading(true);

        try {
          const dateS = periodStartMoment.format('YYYY-MM-DD');
          const dateE = periodEndMoment.format('YYYY-MM-DD');
          const [employeeRecords, branchWorkTimes] = await Promise.all([
            getEmployeeRecords({
              employeeId: currentEmployeeId,
              dateS,
              dateE,
              ActiveBranchId,
            }).unwrap(),
            getBranchWorkTimes({
              branchId: ActiveBranchId,
              dateS,
              dateE,
              ActiveBranchId,
            }).unwrap(),
          ]);
          const { days, currentEmployeeMeta, events } =
            mapIndividualCalendarApiData({
              currentEmployeeId,
              employeeRecords,
              branchWorkTimes,
              visibleDays,
            });

          if (requestIdRef.current !== requestId) {
            return;
          }

          setDays(days);
          setEvents(events);
          setCurrentEmployeeMeta(currentEmployeeMeta);
          setTimeFormat(branchWorkTimes[0]?.JournalFormatTime ?? null);
        } catch (error) {
          if (requestIdRef.current !== requestId) {
            return;
          }

          showNotification(error);
          setDays([]);
          setEvents([]);
          setCurrentEmployeeMeta(null);
        } finally {
          if (requestIdRef.current === requestId) {
            setIsLoading(false);
          }
        }
      },
      [ActiveBranchId, getBranchWorkTimes, getEmployeeRecords, showNotification]
    );

    const startTime = useMemo(
      () =>
        getMinTime(
          days.flatMap((day) => [
            day.column.workStart,
            ...day.events.map((event) => event.start),
          ]),
          DEFAULT_START_TIME
        ),
      [days]
    );

    const endTime = useMemo(
      () =>
        getMaxTime(
          days.flatMap((day) => [
            day.column.workEnd,
            ...day.events.map((event) => event.end),
          ]),
          DEFAULT_END_TIME
        ),
      [days]
    );

    return {
      days,
      events,
      currentEmployeeMeta,
      isLoading,
      startTime,
      endTime,
      timeFormat,
      loadIndividualJournal,
    };
  };
